import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
    sharedPaths,
    SUSY_AUTH_USER_PROJECT_PATH,
    unauthenticatedPaths,
} from '../../../router/constants/route-paths';
import { cognitoSignIn, getToken } from '../../../services/authentication';
import { getCurrentUser, RootState } from '../../../store';
import { UserRoles } from '../../../utils';
import SusyButton from '../../shared/buttons/SusyButton';
import SusyFormField from '../../shared/forms/SusyFormField/SusyFormField';
import SusyInput from '../../shared/forms/SusyInput';
import { SuSyAlert, SusyAlertType } from '../../shared/susyAlert/SusyAlert';
import { AuthPageWrapper } from '../Wrapper/authPageWrapper/AuthPageWrapper';

const LoginWithEmailAndPassword = () => {
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);

    const [showCognitoError, setShowCognitoError] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const currentUser = useSelector(
        (state: RootState) => state.auth.currentUser
    );
    const localization = useSelector(
        (state: RootState) =>
            state.localization.localizationStrings.susy.authentication.signIn
    );

    const afterSignInHandler = async () => {
        const token = await getToken();
        if (token) {
            dispatch(getCurrentUser());
        }
        if (currentUser) {
            const userRole =
                currentUser.userRole.key.toLowerCase() as UserRoles;
            navigateAuthenticatedUser(userRole);
        }
    };

    const navigateAuthenticatedUser = (userRole: UserRoles) => {
        const savedPath = localStorage.getItem(SUSY_AUTH_USER_PROJECT_PATH);

        if (savedPath) {
            if (!savedPath.includes(userRole)) {
                navigate(savedPath);
            }

            localStorage.removeItem(SUSY_AUTH_USER_PROJECT_PATH);
        } else {
            const pathToNavigate = `${userRole}/${sharedPaths.dashboard}`;
            navigate(pathToNavigate);
        }
    };

    useEffect(() => {
        afterSignInHandler();
    }, [currentUser]);

    const signInHandler = async (event: SyntheticEvent) => {
        event.preventDefault();
        setShowCognitoError(false);

        const email = emailRef.current?.value;
        const password = passwordRef.current?.value;

        if (!email || !password) {
            return;
        }

        try {
            await cognitoSignIn(email, password);
            await afterSignInHandler();
        } catch (e: any) {
            setShowCognitoError(true);
        }
    };

    const forgotPasswordHandler = async () => {
        navigate(unauthenticatedPaths.forgotPassword);
    };

    return (
        <AuthPageWrapper
            title={localization.logInTitle}
            subtitle={localization.logInSubtitle}
        >
            <>
                {showCognitoError && (
                    <SuSyAlert
                        type={SusyAlertType.WARNING}
                        text={
                            localization.errorMessages.IncorrectEmailOrPassword
                        }
                        hideIcon={true}
                        className='mb-4'
                    />
                )}

                <form onSubmit={signInHandler}>
                    <SusyFormField
                        label={{
                            fieldIdentifier: `email`,
                            text: localization.email,
                        }}
                    >
                        <SusyInput
                            dataTestId={'sign-in-email-input'}
                            className='form-control mb-4'
                            type='text'
                            name='email'
                            id='email'
                            ref={emailRef}
                        />
                    </SusyFormField>
                    <SusyFormField
                        label={{
                            fieldIdentifier: `password`,
                            text: localization.password,
                        }}
                    >
                        <input
                            data-testid={'sign-in-password-input'}
                            className='form-control mb-4'
                            type='password'
                            name='password'
                            id='password'
                            ref={passwordRef}
                        />
                    </SusyFormField>

                    <SusyButton
                        dataTestId={'sign-in-login-button'}
                        text={localization.loginBtn}
                        fullWidth
                        htmlAttributes={{ type: 'submit' }}
                    />
                </form>
                <p
                    onClick={forgotPasswordHandler}
                    className='text-center mt-3 mb-3 underline-hover'
                >
                    {localization.forgotPassword}
                </p>
            </>
        </AuthPageWrapper>
    );
};

export default LoginWithEmailAndPassword;
